import _ from 'lodash'
import { mapActions, mapState } from 'vuex'
import * as AgentStatus from '../../constants/agent-status'
import { isAgentStatusPinned } from 'src/plugins/helpers/agent-status-pin'
import { isWrapUpHoldRefusal, wrapUpHoldParams } from 'src/plugins/helpers/wrap-up-hold'
import { isSpdSessionOpenRefusal } from 'src/plugins/helpers/spd-session-refusal'

// The statuses api-core refuses an Available write from, because committing it
// would return a live agent's worker to routing mid-call.
const LIVE_CALL_STATUSES = [
  AgentStatus.AGENT_STATUS_ON_CALL,
  AgentStatus.AGENT_STATUS_RINGING
]

// Writes an agent makes by clicking a status choice (menus, missed-call prompt), not the automatic writers.
const STATUS_PICKER_SIGNATURES = ['Talk-ChangeStatus', 'HubSpot-StatusSelector', 'Talk-GoAvailable', 'Talk-StayBusy']

export default {
  data () {
    return {
      loadingAgentStatus: false,
      AgentStatus
    }
  },

  computed: {
    ...mapState('auth', ['profile', 'authenticated']),
    ...mapState('cache', ['currentCompany']),

    agentStatus () {
      return _.get(this.profile, 'agent_status', null)
    },

    isBargingOrWhispering () {
      return this.agentStatus === AgentStatus.AGENT_STATUS_SENTRY
    },

    isAgentOnCall () {
      return this.agentStatus === AgentStatus.AGENT_STATUS_ON_CALL
    },

    // Statuses the call lifecycle owns. A login on a second device must not
    // write over one — the agent is mid-call on the first. Mirrors the list
    // UserPresenceService refuses to auto-flip in api-core.
    isInTransientCallState () {
      return [
        AgentStatus.AGENT_STATUS_ON_CALL,
        AgentStatus.AGENT_STATUS_RINGING,
        AgentStatus.AGENT_STATUS_ON_WRAP_UP,
        AgentStatus.AGENT_STATUS_SENTRY
      ].includes(this.agentStatus)
    },

    // The widgets re-run init on every CRM page load and have no login to gate
    // on, so their automatic write is closed by default: Offline is the only
    // status it may move. Every chosen status and every telephony state stays
    // untouched, without enumerating what to protect.
    //
    // Compared explicitly because AGENT_STATUS_OFFLINE is 0, and agentStatus is
    // null with no profile — which lands on "do not write".
    canAutoSetAvailableOnInit () {
      return this.agentStatus === AgentStatus.AGENT_STATUS_OFFLINE
    }
  },

  methods: {
    color (agentStatus) {
      switch (agentStatus) {
        case AgentStatus.AGENT_STATUS_OFFLINE:
          return 'blue-grey-6'
        case AgentStatus.AGENT_STATUS_ACCEPTING_CALLS:
          return 'green-6'
        case AgentStatus.AGENT_STATUS_NOT_ACCEPTING_CALLS:
          return 'red-6'
        case AgentStatus.AGENT_STATUS_ON_BREAK:
          return 'orange-6'
        case AgentStatus.AGENT_STATUS_ON_CALL:
          return 'light-blue-6'
        case AgentStatus.AGENT_STATUS_ON_WRAP_UP:
          return 'yellow-6'
        case AgentStatus.AGENT_STATUS_RINGING:
          return 'lime-13'
        case AgentStatus.AGENT_STATUS_AUTO_DIAL:
          return 'white'
        case AgentStatus.AGENT_STATUS_SENTRY:
          return 'dark'
        case AgentStatus.AGENT_STATUS_SHARED_POWER_DIALER:
          return 'indigo-6'
        default:
          return 'grey-6'
      }
    },

    getAgentStatus (getTry = 1) {
      if (!this.authenticated) {
        return
      }

      this.$axios.post('/api/v1/profile/get-agent-status', {
        device_info: null
      }).then(res => {
        this.setAgentStatus(res.data.agent_status)
        console.log('Changed agent status [pull]: ', res.data.agent_status)
      }).catch((err) => {
        let errorCode = err?.response?.status
        console.log(err)
        getTry++
        // check if we could get agent status after 3 retries
        if (getTry > 3 || [401, 429].includes(errorCode)) {
          // error
          this.$Sentry.captureException(err)
        } else {
          this.getAgentStatus(getTry)
        }
      })
    },

    // Backend-governed wrap-up exit. User clicks the Finish
    // button → POST to /finish-wrap-up. Backend reads agent_audits and
    // restores the user's pre-call non-telephony status (Offline / On
    // Break / Not Accepting / Available). Frontend only signals intent.
    finishWrapUp (signature = 'Talk-FinishWrapUp') {
      if (!this.authenticated || !this.profile) {
        return Promise.resolve()
      }
      // The Finish button and the wrap-up timer expiry can both call this in
      // the same tick. Reuse the in-flight request so we don't POST
      // /finish-wrap-up twice (changeAgentStatus is debounced for the same
      // reason). Cleared on settle so the next wrap-up can finish normally.
      if (this.$options.finishWrapUpRequest) {
        return this.$options.finishWrapUpRequest
      }
      this.$options.finishWrapUpRequest = this.$axios.post('/api/v1/user/' + this.profile.id + '/finish-wrap-up', wrapUpHoldParams(), {
        headers: { Signature: signature }
      }).then(({ data }) => {
        if (data?.agent_status !== undefined) {
          this.setAgentStatus(data.agent_status)
          this.$VueEvent.fire('user_updated', data)
        }
      }).catch(err => {
        // A failure here is exactly the UX issue this fixes (user appears
        // stuck in wrap-up until FixUserStuckOnStatusJob fires ~30s later).
        // Report to Sentry so we have a leading indicator if the backend
        // endpoint regresses.
        console.log('An error occurred while finishing wrap-up [api]', err)
        this.$Sentry.captureException(err)
      }).finally(() => {
        this.$options.finishWrapUpRequest = null
      })
      return this.$options.finishWrapUpRequest
    },

    // Backend-governed supervisor-leg restore. Supervisor legs
    // (barge/whisper) have no agent_leg_sid binding, so the conferencing
    // participant-leave webhook never fires PostCallStatusResetService
    // for them. Frontend hits this endpoint on hangup.
    restoreAfterSupervisorLeg (signature = 'Talk-RestoreAfterSupervisorLeg') {
      if (!this.authenticated || !this.profile) {
        return Promise.resolve()
      }
      return this.$axios.post('/api/v1/user/' + this.profile.id + '/restore-after-supervisor-leg', {}, {
        headers: { Signature: signature }
      }).then(({ data }) => {
        if (data?.agent_status !== undefined) {
          this.setAgentStatus(data.agent_status)
          this.$VueEvent.fire('user_updated', data)
        }
      }).catch(err => {
        // Same rationale as finishWrapUp: failure here leaves the supervisor
        // visibly stuck in ON_CALL until backend safety nets reconcile.
        console.log('An error occurred while restoring after supervisor leg [api]', err)
        this.$Sentry.captureException(err)
      })
    },

    // Single transport for both engines: the backend's changeAgentStatus
    // pushes the Twilio worker activity itself (with the pending-reservation
    // guard) before committing the local status, so the HTTP endpoint is
    // authoritative for TaskRouter companies too. The old new-engine path
    // rode the Worker SDK socket fire-and-forget — after a network drop the
    // Worker can be dead or mid-reconnect and the change silently went
    // nowhere, which is exactly the "cannot change my status" symptom.
    changeAgentStatus: _.debounce(function (val, forceStatus = false, changeAgentStatusTry = 1, signature = 'Unknown') {
      if (!this.authenticated) {
        return
      }

      // A pinned user has no status but Available, and the endpoint 422s anything
      // else — which this method then retries three times. The automatic writers
      // (worker teardown, wrap-up, reject) reach here with the control hidden, so
      // the guard has to sit on the transport, not only on the menu.
      //
      // Available is refused too while the row reads ON_CALL or RINGING, since
      // committing it would hand a live agent's worker back to routing. Wrap-up
      // is deliberately not in that set: Available is the only way out of an
      // indefinite wrap-up for someone who never opens Talk.
      if (this.isPinnedAgentStatusWrite(val)) {
        console.log('Agent status change skipped [pinned available]: ', val)
        return
      }

      console.log('Changing agent status [api]: ', val)

      // make sure that the session is valid
      if (this.profile) {
        this.loadingAgentStatus = true
        this.$axios.post('/api/v1/user/' + this.profile.id + '/agent-status', {
          agent_status: val,
          ...wrapUpHoldParams()
        }, {
          headers: {
            'Signature': signature
          }
        }).then(({ data }) => {
          this.loadingAgentStatus = false
          // A null body means the backend deferred the change on purpose
          // (e.g. a TaskRouter reservation is pending and Twilio would
          // reject the activity write) — the reservation lifecycle will
          // correct the status, so don't retry against it.
          if (data?.agent_status === undefined) {
            console.log('Agent status change deferred by backend')
            return
          }
          if (forceStatus && val !== data.agent_status) {
            console.log(`Requested Agent status value (${val}) and API response value (${data.agent_status}) is not the same. Retrying request...`)
            this.changeAgentStatus(val, forceStatus, 1, signature)
            return
          }
          this.setAgentStatus(data.agent_status)
          this.$VueEvent.fire('user_updated', data)
          console.log('Changed agent status [api]: ', data.agent_status)
        }).catch(err => {
          // Another tab's Power Dialer session holds the wrap-up: retrying cannot land, the
          // agent needs to know where to end it.
          if (isWrapUpHoldRefusal(err)) {
            this.loadingAgentStatus = false
            this.setAgentStatus(err.response.data.agent_status)
            this.$generalNotification(err.response.data.message, 'warning')
            return
          }
          // A Shared Power Dialer session holds the status. Only a status the agent clicked is
          // told why; boot, worker-teardown and wrap-up writes would toast unprompted.
          if (isSpdSessionOpenRefusal(err)) {
            this.loadingAgentStatus = false
            this.setAgentStatus(err.response.data.agent_status)
            if (STATUS_PICKER_SIGNATURES.includes(signature)) {
              this.$generalNotification(err.response.data.message, 'warning')
            }
            return
          }
          changeAgentStatusTry++
          // error
          console.log('An error occurred while changing agent status [api]', err)
          // check if we have found the communication after 3 retries
          if (changeAgentStatusTry > 3) {
            this.loadingAgentStatus = false
          } else {
            this.changeAgentStatus(val, forceStatus, changeAgentStatusTry, signature)
          }
        })
      }
    }, 500),

    // Mirrors UserController::agentStatus's refusal, so no write is sent that can
    // only come back 422 and be retried three times.
    isPinnedAgentStatusWrite (val) {
      if (!isAgentStatusPinned(this.profile, this.currentCompany)) {
        return false
      }

      return val !== AgentStatus.AGENT_STATUS_ACCEPTING_CALLS ||
        LIVE_CALL_STATUSES.includes(this.agentStatus)
    },

    ...mapActions('auth', ['setAgentStatus', 'setProfile'])
  },

  beforeDestroy () {
    clearInterval(window.agentStatusIntervalId)
  }
}
